// src/pages/CodeOfConduct.jsx
import { Helmet } from "react-helmet-async";
import Layout from "../components/Layout";
import PageTitle from "../components/PageTitle";
import PageText from "../components/PageText";

const declarations = [
  {
    title: "Declaration on Real-Life",
    body: [
      "Real life always comes first. Members are expected to prioritize themselves and their families. The community will strive to support members facing real-life challenges through any beneficial means, including fundraising or assistance.",
    ],
  },
  {
    title: "Declaration on Treason",
    body: [
      "Treason means any malicious act against the community, its members, leaders, allies, or interests, including collusion or conspiracy. Any such act results in immediate and permanent removal.",
    ],
  },
  {
    title: "Declaration on Trolling & Equity",
    body: [
      "Teasing and playful razzing are welcome, but trolling for its own sake is not. Behavior that is hateful, demeaning, antagonistic, or discriminatory based on protected status is strictly prohibited. Again: don’t be a dick.",
    ],
  },
  {
    title: "Declaration on Crybabies",
    body: [
      "Members should not expect others to cater to their beliefs or sensitivities, nor engage in chronic complaining. Hypersensitivity and whining are discouraged as much as trolling. No member will be expected to follow “political correctness” beyond Discord’s Terms of Service, societal norms, and our Declaration on Trolling & Equity.",
    ],
  },
  {
    title: "Declaration on Conflict Resolution",
    body: [
      "Conflict is inevitable in a diverse community. It should be resolved at the lowest level with minimal punitive action. Members should avoid retaliation and first attempt direct, tactful, private resolution. If needed, an Officer or Operative may mediate. If unresolved, leadership may empanel neutral factfinders, recommend resolution, and, if necessary, punitive action.",
    ],
  },
  {
    title: "Declaration on Violations & Due Process",
    body: [
      "CHAOTIK follows progressive discipline: warning, counseling, demotion, suspension, or removal, with punishment fitting the offense. All members retain due process: appeals to leadership and a “day in court.” Any punitive action beyond counseling requires a majority vote of a leadership panel.",
    ],
  },
  {
    title: "Declaration on Comms Behavior",
    body: [
      "Respect the prevailing subject and purpose of comms. Do not hijack ongoing conversations. Repeated hot mic or voice-activation abuse may result in loss of comms privileges. Review the Comms Guidelines channel for details.",
    ],
  },
  {
    title: "Declaration on Rogue Behavior & Teamwork",
    body: [
      "Members are encouraged to hone skills individually, but when in community events, teamwork comes first. Be present and participate, but don’t “go cowboy” and expect rescue.",
    ],
  },
  {
    title: "Declaration on Treasury",
    body: [
      "The community intends to maintain an in-game treasury, with donations tracked in Discord. A ledger and process for requesting funds will be maintained.",
    ],
  },
  {
    title: "Declaration on Participation",
    body: [
      "There are no minimum participation requirements, but participation is considered in matters of promotion, demotion, aid, or discipline. Participation functions as a form of social credit.",
    ],
  },
  {
    title: "Declaration on Minors in the Community",
    body: [
      "Children under 18 may not be full members. Members may permit their children to use this server, but only in limited sections.",
    ],
  },
];

export default function CodeOfConduct() {
  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://chaotik.org/" },
      { "@type": "ListItem", position: 2, name: "Code of Conduct", item: "https://chaotik.org/code-of-conduct" },
    ],
  };

  return (
    <Layout>
      <Helmet>
        <title>CHAOTIK Code of Conduct | Star Citizen Org</title>
        <meta
          name="description"
          content="The CHAOTIK Code of Conduct &mdash; declarations on real life, treason, conflict resolution, comms, teamwork and more. The minimum rules that keep a 24/7 multi-role Star Citizen org running."
        />
        <link rel="canonical" href="https://chaotik.org/code-of-conduct" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>
      <div className="flex flex-col items-center mt-12 mb-24 space-y-12">
        <div className="max-w-5xl w-full bg-black/40 border border-red-500/40 rounded-xl shadow-[0_0_25px_rgba(248,113,113,0.35)] px-8 py-10 text-white text-center backdrop-blur">
          <PageTitle>
            <span className="font-orbitron text-4xl sm:text-5xl text-white">Code of Conduct</span>
          </PageTitle>
          <PageText>
            <p className="mt-4 text-white/80">
              CHAOTIK strives to keep rigid rules minimal in furtherance of our laissez-faire philosophy. Still, certain rules are
              necessary for operability, continuity, and inclusion. By joining this community, you agree to the following
              Declarations, listed in order of importance.
            </p>
          </PageText>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-6xl">
          {declarations.map((declaration) => (
            <div
              key={declaration.title}
              className="bg-black/30 border border-white/10 rounded-lg p-6 text-white shadow-[0_0_15px_rgba(0,0,0,0.45)] backdrop-blur"
            >
              <h2 className="font-orbitron text-2xl text-red-300 uppercase tracking-wide text-center">
                {declaration.title}
              </h2>
              {declaration.body.map((paragraph, index) => (
                <p key={index} className="mt-4 text-sm sm:text-base text-white/80 leading-relaxed text-justify">
                  {paragraph}
                </p>
              ))}
            </div>
          ))}
        </div>

        <div className="max-w-4xl w-full bg-red-900/40 border border-red-400/50 rounded-lg shadow-[0_0_20px_rgba(248,113,113,0.35)] px-6 py-6">
          <p className="text-center text-white/80 text-sm sm:text-base">
            All rules are subject to change at leadership’s discretion, without notice.
          </p>
        </div>
      </div>
    </Layout>
  );
}
